/**
 * Sketch notes (pure, tested in notes.test.ts): the installer's notes pinned to a point or a roof face. A note is a
 * comment thread of kind `note` (the Designer's comments model: GET/POST/PATCH /v1/designs/{id}/comments), so it
 * belongs to the design, shows on every version and in the Designer's comment list, and keeps replies. Its anchor
 * is a zero-size box at the pin (local metres); `in_proposal` / `in_plan_set` print it while it is open (the
 * proposal's design page; the plan set's roof plan PV-2, numbered where it is pinned).
 */
import type { MessageKey } from "../i18n/keys";
import type { S } from "../api/client";
import type { NotePin } from "./types.ts";

export type Thread = S["DesignCommentThread"];

export interface SketchNote {
  id: string;
  /** Its number on the Sketch (open notes, oldest first). */
  n: number;
  text: string;
  at: [number, number];
  faceId: string | null;
  inProposal: boolean;
  inPlanSet: boolean;
  author: string;
  replies: number;
}

/** The open notes of a design's threads, oldest first, numbered from 1. */
export function notesOf(threads: readonly Thread[] | null | undefined): SketchNote[] {
  return (threads ?? [])
    .filter((t) => t.kind === "note" && t.status === "open")
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id))
    .map((t, i) => ({
      id: t.id,
      n: i + 1,
      text: t.comments[0]?.body ?? "",
      at: [(t.anchor.min_xy[0]! + t.anchor.max_xy[0]!) / 2, (t.anchor.min_xy[1]! + t.anchor.max_xy[1]!) / 2],
      faceId: t.face_id ?? null,
      inProposal: !!t.in_proposal,
      inPlanSet: !!t.in_plan_set,
      author: t.comments[0]?.author.name ?? t.created_by.name,
      replies: Math.max(0, t.comments.length - 1),
    }));
}

/** The pins the 2D and 3D views draw (the open note highlighted). */
export function notePins(notes: readonly SketchNote[], selected: string | null): NotePin[] {
  return notes.map((x) => ({ id: x.id, n: x.n, at: x.at, selected: x.id === selected }));
}

const round = (v: number) => Math.round(v * 1000) / 1000;

/** The request that pins a new note (a zero-size anchor at the point; the face when it is on one). */
export function newNoteBody(at: [number, number], faceId: string | null, version: number, text: string, print: { proposal: boolean; planSet: boolean }): S["DesignCommentThreadCreate"] {
  const p = [round(at[0]), round(at[1])];
  return { kind: "note", anchor: { type: "box", min_xy: p, max_xy: p }, face_id: faceId, version_number: version, body: text.trim(), in_proposal: print.proposal, in_plan_set: print.planSet, mentions: [] };
}

/** Where a note is printed, as a catalog key ("Proposal and plan set", "Not printed"). */
export function printedIn(note: Pick<SketchNote, "inProposal" | "inPlanSet">): MessageKey {
  if (note.inProposal && note.inPlanSet) return "sketch.notes.printedBoth";
  if (note.inProposal) return "sketch.notes.printedProposal";
  if (note.inPlanSet) return "sketch.notes.printedPlanSet";
  return "sketch.notes.notPrinted";
}
